import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, RefreshCw, AlertTriangle, CheckCircle,
  Zap, Users, Wrench, Package, Calendar, ChevronDown,
  ChevronRight, Send, Bell, RotateCcw, TrendingDown
} from 'lucide-react';
import api from '../../utils/api';
import {
  getMaterialMRP, generateMaterialMRP, notifyMaterialSupplier, reviseMaterialMRP,
  getManpowerMRP, generateManpowerMRP,
  getMachineryMRP, generateMachineryMRP,
  getToolsMRP, generateToolsMRP,
  getDelaySummary,
  MaterialMRP, ManpowerMRP, MachineryMRP, ToolsMRP, DelaySummary,
} from '../../services/mrp.service';

// ── Types ─────────────────────────────────────────────────────

interface Project { project_id: number; project_code: string; project_name: string; }
type PlanTab = 'material' | 'manpower' | 'machinery' | 'tools';

const STATUS_COLORS: Record<string, string> = {
  PENDING:          'bg-gray-100 text-gray-600',
  NOTIFIED:         'bg-blue-100 text-blue-700',
  REVISION_NEEDED:  'bg-amber-100 text-amber-700',
  ORDERED:          'bg-indigo-100 text-indigo-700',
  DELIVERED:        'bg-green-100 text-green-700',
  CANCELLED:        'bg-red-100 text-red-600',
};

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const fmtWeek = (d: string) => {
  const start = new Date(d);
  const end = new Date(d); end.setDate(end.getDate() + 6);
  return `Week of ${start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

// ── Delay Banner ──────────────────────────────────────────────

const DelayBanner: React.FC<{ delay: DelaySummary; onRevise: () => void; revising: boolean }> = ({ delay, onRevise, revising }) => {
  if (delay.avg_delay_days === 0) return (
    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700">
      <CheckCircle size={15}/> Project is on schedule — no delays detected.
    </div>
  );
  return (
    <div className="flex items-center justify-between bg-amber-50 border border-amber-300 rounded-xl px-4 py-3">
      <div className="flex items-center gap-3">
        <AlertTriangle size={18} className="text-amber-600 flex-shrink-0"/>
        <div>
          <p className="text-sm font-semibold text-amber-800">Project Delay Detected</p>
          <p className="text-xs text-amber-600 mt-0.5">
            {delay.delayed_activities} activities delayed · Average delay: <strong>{delay.avg_delay_days} days</strong> · Max: {delay.max_delay_days} days
          </p>
        </div>
      </div>
      <button onClick={onRevise} disabled={revising}
        className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-40 ml-4 flex-shrink-0">
        {revising ? <RefreshCw size={14} className="animate-spin"/> : <RotateCcw size={14}/>}
        Revise All Schedules
      </button>
    </div>
  );
};

// ── Material MRP Table ────────────────────────────────────────

const MaterialMRPTable: React.FC<{ data: MaterialMRP[]; loading: boolean; onNotify: (ids: number[]) => void; notifying: boolean }> = ({ data, loading, onNotify, notifying }) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());

  const toggleWeek = (wk: string) => setExpandedWeeks(prev => { const s = new Set(prev); s.has(wk) ? s.delete(wk) : s.add(wk); return s; });
  const toggleSelect = (id: number) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // Group by week
  const byWeek = new Map<string, MaterialMRP[]>();
  for (const row of data) {
    if (!byWeek.has(row.week_start_date)) byWeek.set(row.week_start_date, []);
    byWeek.get(row.week_start_date)!.push(row);
  }

  const unnotified = data.filter(r => !r.supplier_notified && r.status !== 'CANCELLED');

  if (loading) return <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse"/>)}</div>;

  if (data.length === 0) return (
    <div className="text-center py-16 text-gray-400">
      <Package size={40} className="mx-auto mb-3 opacity-30"/>
      <p className="font-medium">No material requirements generated yet</p>
      <p className="text-sm mt-1">Click "Generate MRP" to create the requirement plan from activity schedules</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
        <div className="flex items-center gap-6 text-sm">
          <span className="text-gray-500">{data.length} requirements across {byWeek.size} weeks</span>
          <span className="font-semibold text-gray-900">Total Est. Cost: {fmt(data.reduce((s, r) => s + (parseFloat(String(r.estimated_cost)) || 0), 0))}</span>
          {unnotified.length > 0 && <span className="text-amber-600 font-medium">{unnotified.length} not yet notified</span>}
        </div>
        {selected.size > 0 && (
          <button onClick={() => { onNotify(Array.from(selected)); setSelected(new Set()); }} disabled={notifying}
            className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40">
            {notifying ? <RefreshCw size={13} className="animate-spin"/> : <Send size={13}/>}
            Notify Selected ({selected.size})
          </button>
        )}
        {selected.size === 0 && unnotified.length > 0 && (
          <button onClick={() => onNotify(unnotified.map(r => r.mrp_id))} disabled={notifying}
            className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40">
            {notifying ? <RefreshCw size={13} className="animate-spin"/> : <Bell size={13}/>}
            Notify All ({unnotified.length})
          </button>
        )}
      </div>

      {/* Week groups */}
      {Array.from(byWeek.entries()).map(([wk, rows]) => {
        const isOpen = expandedWeeks.has(wk) || expandedWeeks.size === 0;
        const weekTotal = rows.reduce((s, r) => s + (parseFloat(String(r.estimated_cost)) || 0), 0);
        const pendingRows = rows.filter(r => !r.supplier_notified);
        return (
          <div key={wk} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button onClick={() => toggleWeek(wk)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50">
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown size={16} className="text-gray-400"/> : <ChevronRight size={16} className="text-gray-400"/>}
                <div className="text-left">
                  <p className="font-semibold text-gray-800 text-sm">{fmtWeek(wk)}</p>
                  <p className="text-xs text-gray-400">{rows.length} materials · {pendingRows.length} to notify</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-bold text-gray-900">{fmt(weekTotal)}</span>
                {pendingRows.length > 0 && (
                  <button onClick={e => { e.stopPropagation(); onNotify(pendingRows.map(r => r.mrp_id)); }} disabled={notifying}
                    className="text-xs text-blue-600 border border-blue-300 px-2 py-1 rounded hover:bg-blue-50">
                    Notify Week
                  </button>
                )}
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-gray-100 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 text-gray-500">
                    <th className="px-4 py-2 w-8"><input type="checkbox" onChange={e => { if (e.target.checked) setSelected(prev => new Set(Array.from(prev).concat(rows.map(r => r.mrp_id)))); else setSelected(prev => { const s = new Set(Array.from(prev)); rows.forEach(r => s.delete(r.mrp_id)); return s; }); }}/></th>
                    <th className="text-left px-4 py-2">Material / Brand</th>
                    <th className="text-left px-4 py-2">Quality</th>
                    <th className="text-right px-4 py-2">Order Qty</th>
                    <th className="text-left px-4 py-2">Best Supplier</th>
                    <th className="text-right px-4 py-2">Required By</th>
                    <th className="text-right px-4 py-2">Dispatch By</th>
                    <th className="text-right px-4 py-2">Est. Cost</th>
                    <th className="text-center px-4 py-2">Status</th>
                  </tr></thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.mrp_id} className={`border-t border-gray-100 hover:bg-gray-50 ${selected.has(row.mrp_id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-2.5"><input type="checkbox" checked={selected.has(row.mrp_id)} onChange={() => toggleSelect(row.mrp_id)}/></td>
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-gray-800">{row.mat_name}</p>
                          <p className="text-gray-400">{row.mat_code}{row.brand_name ? ` · ${row.brand_name}` : ''}</p>
                        </td>
                        <td className="px-4 py-2.5 text-gray-500">{row.quality_tier || '—'}</td>
                        <td className="px-4 py-2.5 text-right font-medium">{Number(row.order_quantity).toLocaleString()} {row.unit_of_measure}</td>
                        <td className="px-4 py-2.5">
                          {row.supplier_name ? (
                            <div>
                              <p className="text-gray-800 font-medium">{row.supplier_name}</p>
                              {row.supplier_mobile && <p className="text-gray-400">{row.supplier_mobile}</p>}
                            </div>
                          ) : <span className="text-amber-500">No supplier set</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700">{fmtDate(row.required_by_date)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-500">{fmtDate(row.dispatch_by_date)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{fmt(row.estimated_cost)}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[row.status] || 'bg-gray-100 text-gray-500'}`}>{row.status}</span>
                          {row.supplier_notified && <p className="text-green-600 text-xs mt-0.5">✓ {fmtDate(row.notification_date)}</p>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Manpower MRP Table ────────────────────────────────────────

const ManpowerTable: React.FC<{ data: ManpowerMRP[]; loading: boolean }> = ({ data, loading }) => {
  if (loading) return <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse"/>)}</div>;
  if (data.length === 0) return <div className="text-center py-16 text-gray-400"><Users size={40} className="mx-auto mb-3 opacity-30"/><p className="font-medium">No manpower requirements generated yet</p></div>;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="bg-gray-50 text-xs text-gray-500">
          <th className="text-left px-4 py-3">Skill</th>
          <th className="text-left px-4 py-3">Week</th>
          <th className="text-right px-4 py-3">Workers</th>
          <th className="text-right px-4 py-3">Days</th>
          <th className="text-left px-4 py-3">Contractor</th>
          <th className="text-center px-4 py-3">Status</th>
        </tr></thead>
        <tbody>
          {data.map(row => (
            <tr key={row.mrp_id} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3"><p className="font-medium text-gray-800">{row.skill_name || row.skill_code}</p><p className="text-xs text-gray-400">{row.skill_category}</p></td>
              <td className="px-4 py-3 text-gray-600 text-xs">{fmtWeek(row.week_start_date)}</td>
              <td className="px-4 py-3 text-right font-semibold">{row.workers_required}</td>
              <td className="px-4 py-3 text-right text-gray-600">{row.days_required} days</td>
              <td className="px-4 py-3">{row.contractor_name ? <div><p className="font-medium text-gray-800">{row.contractor_name}</p>{row.contractor_mobile && <p className="text-xs text-gray-400">{row.contractor_mobile}</p>}</div> : <span className="text-amber-500 text-sm">No contractor</span>}</td>
              <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[row.status] || 'bg-gray-100 text-gray-500'}`}>{row.status}</span>{row.contractor_notified && <p className="text-green-600 text-xs mt-0.5">✓ Notified</p>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Machinery MRP Table ───────────────────────────────────────

const MachineryTable: React.FC<{ data: MachineryMRP[]; loading: boolean }> = ({ data, loading }) => {
  if (loading) return <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse"/>)}</div>;
  if (data.length === 0) return <div className="text-center py-16 text-gray-400"><Wrench size={40} className="mx-auto mb-3 opacity-30"/><p className="font-medium">No machinery requirements generated yet</p></div>;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="bg-gray-50 text-xs text-gray-500">
          <th className="text-left px-4 py-3">Machine Type</th>
          <th className="text-left px-4 py-3">Week</th>
          <th className="text-right px-4 py-3">Quantity</th>
          <th className="text-right px-4 py-3">Days</th>
          <th className="text-right px-4 py-3">Hours/Day</th>
          <th className="text-center px-4 py-3">Status</th>
        </tr></thead>
        <tbody>
          {data.map(row => (
            <tr key={row.mrp_id} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3 font-medium text-gray-800">{row.mch_type}</td>
              <td className="px-4 py-3 text-gray-600 text-xs">{fmtWeek(row.week_start_date)}</td>
              <td className="px-4 py-3 text-right font-semibold">{row.quantity_needed}</td>
              <td className="px-4 py-3 text-right text-gray-600">{row.days_required}</td>
              <td className="px-4 py-3 text-right text-gray-600">{row.hours_per_day}h</td>
              <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[row.status] || 'bg-gray-100 text-gray-500'}`}>{row.status}</span>{row.vendor_notified && <p className="text-green-600 text-xs mt-0.5">✓ Notified</p>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Tools MRP Table ───────────────────────────────────────────

const ToolsTable: React.FC<{ data: ToolsMRP[]; loading: boolean }> = ({ data, loading }) => {
  if (loading) return <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse"/>)}</div>;
  if (data.length === 0) return <div className="text-center py-16 text-gray-400"><Wrench size={40} className="mx-auto mb-3 opacity-30"/><p className="font-medium">No tool requirements generated yet</p></div>;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="bg-gray-50 text-xs text-gray-500">
          <th className="text-left px-4 py-3">Tool Category</th>
          <th className="text-left px-4 py-3">Week</th>
          <th className="text-right px-4 py-3">Quantity</th>
          <th className="text-center px-4 py-3">Status</th>
        </tr></thead>
        <tbody>
          {data.map(row => (
            <tr key={row.mrp_id} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3 font-medium text-gray-800">{row.tls_category}</td>
              <td className="px-4 py-3 text-gray-600 text-xs">{fmtWeek(row.week_start_date)}</td>
              <td className="px-4 py-3 text-right font-semibold">{row.quantity_needed}</td>
              <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[row.status] || 'bg-gray-100 text-gray-500'}`}>{row.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────

const ResourcePlanPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [tab, setTab] = useState<PlanTab>('material');
  const [delay, setDelay] = useState<DelaySummary | null>(null);
  const [materialMRP, setMaterialMRP] = useState<MaterialMRP[]>([]);
  const [manpowerMRP, setManpowerMRP] = useState<ManpowerMRP[]>([]);
  const [machineryMRP, setMachineryMRP] = useState<MachineryMRP[]>([]);
  const [toolsMRP, setToolsMRP] = useState<ToolsMRP[]>([]);
  const [loadingTab, setLoadingTab] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [revising, setRevising] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    api.get('/projects').then((r: any) => {
      const list: Project[] = r.data.data || [];
      setProjects(list);
      if (list.length === 1) setProjectId(list[0].project_id);
    }).catch(() => {});
  }, []);

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const loadDelay = useCallback(async (pid: number) => {
    try { setDelay(await getDelaySummary(pid)); } catch { }
  }, []);

  const loadTab = useCallback(async (pid: number, t: PlanTab) => {
    setLoadingTab(true);
    try {
      if (t === 'material') setMaterialMRP(await getMaterialMRP(pid));
      if (t === 'manpower') setManpowerMRP(await getManpowerMRP(pid));
      if (t === 'machinery') setMachineryMRP(await getMachineryMRP(pid));
      if (t === 'tools') setToolsMRP(await getToolsMRP(pid));
    } catch { } finally { setLoadingTab(false); }
  }, []);

  useEffect(() => {
    if (projectId) { loadDelay(projectId); loadTab(projectId, tab); }
  }, [projectId, tab, loadDelay, loadTab]);

  const handleGenerate = async () => {
    if (!projectId) return;
    setGenerating(true);
    try {
      let result: any;
      if (tab === 'material') result = await generateMaterialMRP(projectId);
      if (tab === 'manpower') result = await generateManpowerMRP(projectId);
      if (tab === 'machinery') result = await generateMachineryMRP(projectId);
      if (tab === 'tools') result = await generateToolsMRP(projectId);
      if (result?.message) { showMsg('error', result.message); }
      else { showMsg('success', `Generated ${result?.generated || 0} requirement${result?.generated !== 1 ? 's' : ''}`); }
      await loadTab(projectId, tab);
    } catch (e: any) { showMsg('error', e?.response?.data?.message || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleNotify = async (ids: number[]) => {
    if (!projectId) return;
    setNotifying(true);
    try {
      const r = await notifyMaterialSupplier(ids);
      showMsg('success', `Notified ${r.notified} suppliers`);
      await loadTab(projectId, 'material');
    } catch (e: any) { showMsg('error', e?.response?.data?.message || 'Notification failed'); }
    finally { setNotifying(false); }
  };

  const handleRevise = async () => {
    if (!projectId) return;
    setRevising(true);
    try {
      const r = await reviseMaterialMRP(projectId);
      showMsg('success', `Revised ${r.revised} requirements by ${r.delay_days} days`);
      await Promise.all([loadDelay(projectId), loadTab(projectId, tab)]);
    } catch (e: any) { showMsg('error', e?.response?.data?.message || 'Revision failed'); }
    finally { setRevising(false); }
  };

  const TAB_CONFIG: Record<PlanTab, { label: string; icon: React.ReactNode; count: number }> = {
    material: { label: 'Materials', icon: <Package size={15}/>, count: materialMRP.length },
    manpower: { label: 'Manpower', icon: <Users size={15}/>, count: manpowerMRP.length },
    machinery: { label: 'Machinery', icon: <Wrench size={15}/>, count: machineryMRP.length },
    tools:    { label: 'Tools', icon: <Zap size={15}/>, count: toolsMRP.length },
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center"><ClipboardList size={18} className="text-white"/></div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Resource Requirement Plan</h1>
              <p className="text-xs text-gray-400">Week-wise consolidated plan with lowest-cost supplier assignment</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select value={projectId ?? ''} onChange={e => setProjectId(e.target.value ? parseInt(e.target.value) : null)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-indigo-500 focus:outline-none min-w-52">
              <option value="">Select project…</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_code} — {p.project_name}</option>)}
            </select>
            {projectId && (
              <button onClick={handleGenerate} disabled={generating}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-40">
                {generating ? <RefreshCw size={15} className="animate-spin"/> : <TrendingDown size={15}/>}
                Generate {TAB_CONFIG[tab].label} MRP
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {(Object.keys(TAB_CONFIG) as PlanTab[]).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg font-medium transition-colors ${tab === t ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {TAB_CONFIG[t].icon} {TAB_CONFIG[t].label} {TAB_CONFIG[t].count > 0 && `(${TAB_CONFIG[t].count})`}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 p-6 space-y-4">
        {/* Flash message */}
        {msg && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {msg.type === 'success' ? <CheckCircle size={15}/> : <AlertTriangle size={15}/>} {msg.text}
          </div>
        )}

        {!projectId ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <ClipboardList size={48} className="mb-3 opacity-30"/>
            <p className="text-lg font-medium">Select a project to view requirements</p>
          </div>
        ) : (
          <>
            {/* Delay banner (only on material tab) */}
            {tab === 'material' && delay && (
              <DelayBanner delay={delay} onRevise={handleRevise} revising={revising}/>
            )}

            {/* Tab content */}
            {tab === 'material' && <MaterialMRPTable data={materialMRP} loading={loadingTab} onNotify={handleNotify} notifying={notifying}/>}
            {tab === 'manpower' && <ManpowerTable data={manpowerMRP} loading={loadingTab}/>}
            {tab === 'machinery' && <MachineryTable data={machineryMRP} loading={loadingTab}/>}
            {tab === 'tools' && <ToolsTable data={toolsMRP} loading={loadingTab}/>}
          </>
        )}
      </div>
    </div>
  );
};

export default ResourcePlanPage;
